import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface RadioOption {
  value: string;
  label: string;
  /** Optional small helper text under the label. */
  description?: string;
}

interface RadioGroupProps {
  name: string;
  value: string | null;
  onChange: (value: string) => void;
  options: ReadonlyArray<RadioOption>;
  /** Stack vertically (default) or horizontally if true. */
  inline?: boolean;
  className?: string;
  disabled?: boolean;
  /** Optional aria-labelledby pointing at a heading. */
  labelledBy?: string;
}

/**
 * Accessible radio group. Visually styled radios with pastel checked state.
 * Each option is a label wrapping a native input + a styled disc.
 */
export function RadioGroup({
  name,
  value,
  onChange,
  options,
  inline,
  className,
  disabled,
  labelledBy,
}: RadioGroupProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className={cn(inline ? 'flex flex-wrap gap-2' : 'flex flex-col gap-2', className)}
    >
      {options.map((opt) => (
        <RadioRow
          key={opt.value}
          name={name}
          option={opt}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

interface RadioRowProps {
  name: string;
  option: RadioOption;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}

const RadioRow = forwardRef<HTMLInputElement, RadioRowProps & InputHTMLAttributes<HTMLInputElement>>(
  ({ name, option, checked, onChange, disabled }, ref) => {
    return (
      <label
        className={cn(
          'group inline-flex items-start gap-2.5 cursor-pointer rounded-md border px-3 py-2',
          'transition-colors',
          checked
            ? 'border-lavender-300 bg-lavender-50'
            : 'border-ink-200 bg-white hover:border-lavender-200 hover:bg-lavender-50/40',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="relative inline-flex items-center justify-center mt-0.5">
          <input
            ref={ref}
            type="radio"
            name={name}
            value={option.value}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={cn(
              'h-4 w-4 rounded-full border bg-white transition-colors',
              checked ? 'border-lavender-500' : 'border-ink-300 group-hover:border-lavender-400',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-lavender-400 peer-focus-visible:ring-offset-2',
            )}
          />
          {checked && (
            <span
              aria-hidden="true"
              className="absolute h-2 w-2 rounded-full bg-lavender-500 pointer-events-none"
            />
          )}
        </span>
        <span className="flex-1 leading-tight select-none">
          <span className="block text-sm text-ink-800">{option.label}</span>
          {option.description && (
            <span className="block text-xs text-ink-500 mt-0.5">{option.description}</span>
          )}
        </span>
      </label>
    );
  },
);
RadioRow.displayName = 'RadioRow';
